import React, { useCallback, useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { LegendProps } from '@nivo/legends';
import { calculateTimeSeriesTicks } from '../calculateTimeSeriesTicks';
import { CommonLineChartProps } from '../CommonLineChartProps';
import { TimeSeriesLineTooltip } from '../SensorChartTooltip/TimeSeriesLineTooltip';
import { DateServiceSingleton } from 'App/utils/DateService';
import { SentryApiClient } from '_generated/api';
import { BandLayer } from '../../../BandLayer/BandLayer';
import { ClickedNoteLayer } from 'SensorReadings/components/ClickedNoteLayer';
import { NoteLayer } from 'SensorReadings/components/NoteLayer';

export interface Coords {
  x: number | Date | string;
  y: number | string | null;
  actualDate?: Date;
  actualValue?: number;
  rangeId?: string;
  temperature?: number;
  tSens?: number;
}

export interface ChartDataProps {
  id: string;
  isPrimarySensor: boolean;
  color: string;
  data: Coords[];
  dateOffset: number;
  events?: SentryApiClient.EventLogDTO[];
}

export interface SensorReadingBands {
  redHigh?: number;
  amberHigh?: number;
  amberLow?: number;
  redLow?: number;
}

export interface TimeSeriesLineChartProps {
  lines: ChartDataProps[];
  sensor: SentryApiClient.SensorDTO;
  yLabel: string;
  yUnits?: string;
  xLabel?: string;
  xUnits?: string;
  stacked?: boolean;
  leftYMin: number;
  leftYMax: number;
  rightYMin?: number;
  rightYMax?: number;
  point?: boolean;
  dateFormat?: string;
  earliestDate: Date;
  latestDate: Date;
  timezone: string;
  bands?: SensorReadingBands;
  showEventLogs?: boolean;
  showTSensChart?: boolean;
}

export const LineChart: React.FC<TimeSeriesLineChartProps> = ({
  yLabel,
  yUnits,
  xLabel,
  lines,
  sensor,
  leftYMin,
  leftYMax,
  rightYMin,
  rightYMax,
  earliestDate,
  latestDate,
  bands,
  timezone,
  showEventLogs,
  showTSensChart,
}) => {
  const colors = lines.map((l) => {
    return l.color;
  });

  const rightAxisFormatter = useCallback(
    (value: number) => {
      if (rightYMin === undefined || rightYMax === undefined) {
        return 0;
      }

      const displayRange = rightYMax - rightYMin;
      const ratio = displayRange / (leftYMax - leftYMin);
      const rawLabelValue = (value - leftYMin) * ratio + rightYMin;

      return displayRange > 8
        ? Math.round(rawLabelValue)
        : Number(rawLabelValue.toFixed(1));
    },
    [leftYMin, leftYMax, rightYMin, rightYMax]
  );

  return (
    <ResponsiveLine
      {...CommonLineChartProps}
      data={lines}
      colors={colors}
      xFormat={(data: any) => {
        return DateServiceSingleton.GetDateTime(
          data,
          false,
          'DD/MM/YYYY h:mm A ZZ'
        );
      }}
      lineWidth={1.6}
      axisBottom={{
        ...calculateTimeSeriesTicks(earliestDate, latestDate),
        legend: xLabel,
        legendOffset: 40,
        legendPosition: 'middle',
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: yLabel,
        legendOffset: -50,
        legendPosition: 'middle',
      }}
      axisRight={
        rightYMin !== undefined && rightYMax !== undefined && rightYMax !== 0
          ? {
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: yLabel,
              legendOffset: 50,
              legendPosition: 'middle',
              format: rightAxisFormatter,
            }
          : undefined
      }
      yScale={{
        type: 'linear',
        min: leftYMin,
        max: leftYMax,
      }}
      areaOpacity={0.2}
      pointSize={0}
      animate={false}
      sliceTooltip={({ slice }) => {
        return (
          <TimeSeriesLineTooltip
            points={slice.points}
            dateOffsets={lines.map((l) => ({ id: l.id, offset: l.dateOffset }))}
            yUnit={yUnits}
            showTSens={showTSensChart}
          />
        );
      }}
      enableGridX={false}
      layers={[
        'grid',
        'markers',
        'axes',
        'areas',
        'crosshair',
        'lines',
        'slices',
        'points',
        (props) => (
          <BandLayer
            redHigh={bands?.redHigh}
            amberHigh={bands?.amberHigh}
            amberLow={bands?.amberLow}
            redLow={bands?.redLow}
            {...props}
          />
        ),
        (props) => (
          <NoteLayer
            chartData={lines}
            timezone={timezone}
            enable={showEventLogs}
            {...props}
          />
        ),
        (props) => (
          <ClickedNoteLayer chartData={lines} timezone={timezone} {...props} />
        ),
        'legends',
      ]}
    />
  );
};
